<script setup>
import { useDialogPluginComponent } from 'quasar';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import HrBasicDialog from '../HrBasicDialog.vue';
import HrBtn from '../HrBtn.vue';

/**
 * Alert dialog presented via alertDialog() with a message and single OK button.
 *
 * @status internal
 * @group cards-dialogs
 */
defineOptions({ name: 'HrAlertDialog' });

const props = defineProps({
  /** Dialog headline. Omit to show no headline. */
  title: {
    type: String,
    default: null,
  },
  /** Supporting text providing context for the alert. */
  message: {
    type: String,
    required: true,
  },
  /** Label for the dismiss action button. Defaults to the common.buttonOk translation. */
  okLabel: {
    type: String,
    default: null,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent();

const { t } = useI18n();
const effectiveOkLabel = computed(() => props.okLabel ?? t('common.buttonOk'));
</script>

<template>
  <hr-basic-dialog
    ref="dialogRef"
    @hide="onDialogHide"
  >
    <div class="hr-dialog">
      <p
        v-if="title"
        class="hr-dialog__headline hr-headline-small"
      >
        {{ title }}
      </p>
      <p class="hr-dialog__supporting-text hr-body-medium">
        {{ message }}
      </p>
      <div class="hr-dialog__actions">
        <!-- @todo When the legacy theme is removed: remove :legacy-props -->
        <hr-btn
          variant="text"
          :label="effectiveOkLabel"
          :legacy-props="{ flat: true, color: 'primary' }"
          data-autofocus
          @click="() => onDialogOK()"
        />
      </div>
    </div>
  </hr-basic-dialog>
</template>

<style lang="scss" scoped>
.hr-dialog {
  background: var(--app-surface-container-high);
  border-radius: var(--app-shape-corner-extra-large);
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.hr-dialog__headline {
  color: var(--app-on-surface);
  margin: 0 0 16px;
}

.hr-dialog__supporting-text {
  color: var(--app-on-surface-variant);
  margin: 0;
  /* Supporting text is plain text; honor authored line breaks (\n) without allowing HTML. */
  white-space: pre-line;
}

.hr-dialog__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}
</style>
